import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Avatar,
  Tag,
  Progress,
  Button,
  Space,
  Badge,
  Tooltip,
  Modal,
  Steps,
  Alert,
  Statistic,
  List,
  Collapse,
  Checkbox,
  Input,
  Select,
  Spin,
  message,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  ClearOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiService, Client } from '../../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

// Types
interface OnboardingClient {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tier: 'Basic' | 'Standard' | 'Premium' | 'Elite';
  stage: 'leads' | 'contract' | 'payment' | 'onboarding' | 'completed' | 'stalled';
  joinDate: string;
  progress: number;
  assignedCoach?: string;
  notes: string;
  priority: 'low' | 'medium' | 'high';
  value: number;
  daysInStage: number;
  completedSteps: string[];
}

type PipelineStage = {
  key: string;
  title: string;
  description: string;
  color: string;
  icon: React.ReactNode;
  clients: OnboardingClient[];
};

const onboardingSteps = [
  'Initial Contact',
  'Consultation Scheduled',
  'Contract Sent',
  'Contract Signed',
  'Payment Processed',
  'Coach Assigned',
  'First Session Scheduled',
  'Program Created',
];

const OnboardingPipeline: React.FC = () => {
  const [clients, setClients] = useState<OnboardingClient[]>([]);
  const [filteredClients, setFilteredClients] = useState<OnboardingClient[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState<OnboardingClient | null>(null);
  const [showStalledModal, setShowStalledModal] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    searchText: '',
    stages: [] as string[],
    priorities: [] as string[],
    tiers: [] as string[],
    coaches: [] as string[],
    daysInStageRange: null as [number, number] | null,
    valueRange: null as [number, number] | null,
  });

  // Convert Client to OnboardingClient
  const mapClientToOnboardingClient = (client: Client): OnboardingClient => {
    const user = client.user;
    const daysCreated = Math.floor((Date.now() - new Date(client.createdAt).getTime()) / (1000 * 60 * 60 * 24));
    
    // Map onboarding progress to pipeline stage
    let stage: OnboardingClient['stage'] = 'leads';
    if (client.onboardingProgress >= 100) {
      stage = 'completed';
    } else if (client.onboardingProgress >= 80) {
      stage = 'onboarding';
    } else if (client.paymentStatus === 'COMPLETED') {
      stage = 'payment';
    } else if (client.paymentStatus === 'PENDING' || client.paymentStatus === 'PROCESSING') {
      stage = 'contract';
    } else if (client.onboardingProgress > 0) {
      stage = 'contract';
    }

    // Determine priority based on tier and payment status
    let priority: OnboardingClient['priority'] = 'medium';
    if (client.tier === 'ELITE' || client.tier === 'PREMIUM') {
      priority = 'high';
    } else if (client.tier === 'BASIC') {
      priority = 'low';
    }

    return {
      id: client.id,
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phoneNumber || '',
      tier: client.tier.charAt(0) + client.tier.slice(1).toLowerCase() as any,
      stage,
      joinDate: client.createdAt.split('T')[0],
      progress: client.onboardingProgress,
      assignedCoach: client.assignedCoach?.user ? 
        `${client.assignedCoach.user.firstName || ''} ${client.assignedCoach.user.lastName || ''}`.trim() : 
        undefined,
      notes: client.notes || 'No notes available',
      priority,
      value: client.agreedPrice || 0,
      daysInStage: daysCreated,
      completedSteps: getCompletedSteps(client.onboardingProgress),
    };
  };

  // Get completed steps based on progress
  const getCompletedSteps = (progress: number): string[] => {
    const steps = [];
    if (progress >= 12.5) steps.push('Initial Contact');
    if (progress >= 25) steps.push('Consultation Scheduled');
    if (progress >= 37.5) steps.push('Contract Sent');
    if (progress >= 50) steps.push('Contract Signed');
    if (progress >= 62.5) steps.push('Payment Processed');
    if (progress >= 75) steps.push('Coach Assigned');
    if (progress >= 87.5) steps.push('First Session Scheduled');
    if (progress >= 100) steps.push('Program Created');
    return steps;
  };

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching clients for onboarding pipeline...');
      const response = await apiService.getClients({
        page: 1,
        limit: 100
      });
      
      if (response.success && response.data) {
        const clientsData = response.data.clients || response.data;
        const onboardingClients = clientsData.map(mapClientToOnboardingClient);
        setClients(onboardingClients);
        setFilteredClients(onboardingClients);
        message.success(`Loaded ${onboardingClients.length} clients for onboarding pipeline`);
      } else {
        throw new Error(response.error || 'Failed to fetch clients');
      }
    } catch (err: any) {
      console.error('❌ Failed to fetch clients for onboarding:', err);
      setError(err.message || 'Failed to fetch clients');
      message.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter function
  const applyFilters = useCallback(() => {
    let filtered = [...clients];

    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(client => 
        client.firstName.toLowerCase().includes(searchLower) ||
        client.lastName.toLowerCase().includes(searchLower) ||
        client.email.toLowerCase().includes(searchLower)
      );
    }

    // Stage filter
    if (filters.stages.length > 0) {
      filtered = filtered.filter(client => filters.stages.includes(client.stage));
    }

    // Priority filter
    if (filters.priorities.length > 0) {
      filtered = filtered.filter(client => filters.priorities.includes(client.priority));
    }

    // Tier filter
    if (filters.tiers.length > 0) {
      filtered = filtered.filter(client => filters.tiers.includes(client.tier));
    }

    // Coach filter
    if (filters.coaches.length > 0) {
      filtered = filtered.filter(client => 
        client.assignedCoach && filters.coaches.includes(client.assignedCoach)
      );
    }

    // Days in stage filter
    if (filters.daysInStageRange) {
      const [min, max] = filters.daysInStageRange;
      filtered = filtered.filter(client => 
        client.daysInStage >= min && client.daysInStage <= max
      );
    }

    // Value range filter
    if (filters.valueRange) {
      const [min, max] = filters.valueRange;
      filtered = filtered.filter(client => 
        client.value >= min && client.value <= max
      );
    }

    setFilteredClients(filtered);
  }, [clients, filters]);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      searchText: '',
      stages: [],
      priorities: [],
      tiers: [],
      coaches: [],
      daysInStageRange: null,
      valueRange: null,
    });
  };

  // Pipeline stages configuration
  const stages: PipelineStage[] = [
    {
      key: 'leads',
      title: 'New Leads',
      description: 'Initial inquiries and prospects',
      color: '#1890ff',
      icon: <UserOutlined />,
      clients: filteredClients.filter(c => c.stage === 'leads'),
    },
    {
      key: 'contract',
      title: 'Contract Pending',
      description: 'Contract sent, awaiting signature',
      color: '#fa8c16',
      icon: <EditOutlined />,
      clients: filteredClients.filter(c => c.stage === 'contract'),
    },
    {
      key: 'payment',
      title: 'Payment Pending',
      description: 'Contract signed, processing payment',
      color: '#722ed1',
      icon: <DollarOutlined />,
      clients: filteredClients.filter(c => c.stage === 'payment'),
    },
    {
      key: 'onboarding',
      title: 'Onboarding In Progress',
      description: 'Setting up program and coach assignment',
      color: '#13c2c2',
      icon: <CalendarOutlined />,
      clients: filteredClients.filter(c => c.stage === 'onboarding'),
    },
    {
      key: 'completed',
      title: 'Onboarded',
      description: 'Successfully completed onboarding',
      color: '#52c41a',
      icon: <CheckCircleOutlined />,
      clients: filteredClients.filter(c => c.stage === 'completed'),
    },
    {
      key: 'stalled',
      title: 'Stalled/Issues',
      description: 'Requires attention or follow-up',
      color: '#f5222d',
      icon: <WarningOutlined />,
      clients: filteredClients.filter(c => c.stage === 'stalled'),
    },
     ];

  // Handle stalled clients review
  const handleReviewStalled = () => {
    const stalledClients = filteredClients.filter(c => c.stage === 'stalled');
    if (stalledClients.length > 0) {
      setShowStalledModal(true);
    }
  };

  // Client card component
  const ClientCard: React.FC<{ client: OnboardingClient }> = ({ client }) => {
    const getPriorityColor = (priority: string) => {
      switch (priority) {
        case 'high': return '#f5222d';
        case 'medium': return '#fa8c16';
        case 'low': return '#52c41a';
        default: return '#d9d9d9';
      }
    };



    const getTierColor = (tier: string) => {
      switch (tier) {
        case 'Basic': return 'default';
        case 'Standard': return 'blue';
        case 'Premium': return 'gold';
        case 'Elite': return 'red';
        default: return 'default';
      }
    };

    return (
      <Card
        size="small"
        style={{ 
          marginBottom: 8, 
          cursor: 'pointer',
          borderLeft: `4px solid ${getPriorityColor(client.priority)}`,
        }}
        onClick={() => {
          setSelectedClient(client);
          setIsModalVisible(true);
        }}
        hoverable
      >
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>{client.firstName} {client.lastName}</Text>
            <Tag color={getTierColor(client.tier)} style={{ fontSize: 10 }}>
              {client.tier}
            </Tag>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Space size="small">
            <MailOutlined style={{ color: '#1890ff' }} />
            <Text style={{ fontSize: 12 }} ellipsis>
              {client.email}
            </Text>
          </Space>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Progress 
            percent={client.progress} 
            size="small" 
            showInfo={false}
            strokeColor={getPriorityColor(client.priority)}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <Text style={{ fontSize: 11, color: '#666' }}>
              {client.progress}% Complete
            </Text>
            <Text style={{ fontSize: 11, color: '#666' }}>
              ${client.value}
            </Text>
          </div>
        </div>

        {client.assignedCoach && (
          <div style={{ marginBottom: 4 }}>
            <Space size="small">
              <Avatar size={16} icon={<UserOutlined />} />
              <Text style={{ fontSize: 11, color: '#666' }}>
                {client.assignedCoach}
              </Text>
            </Space>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Badge 
            count={client.daysInStage} 
            style={{ backgroundColor: client.daysInStage > 5 ? '#f5222d' : '#52c41a' }}
            title={`${client.daysInStage} days in this stage`}
          />
          <Tooltip title={client.notes}>
            <Text style={{ fontSize: 11, color: '#666' }} ellipsis>
              {client.notes.substring(0, 20)}...
            </Text>
          </Tooltip>
        </div>
      </Card>
    );
  };

  // Pipeline statistics  
  const totalClients = filteredClients.length;
  const totalValue = filteredClients.reduce((sum, client) => sum + client.value, 0);
  const avgDaysInStage = totalClients > 0 ? Math.round(filteredClients.reduce((sum, client) => sum + client.daysInStage, 0) / totalClients) : 0;
  const conversionRate = totalClients > 0 ? Math.round((filteredClients.filter(c => c.stage === 'completed').length / totalClients) * 100) : 0;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Onboarding Pipeline</Title>
        <Text type="secondary">
          Track and manage client onboarding process through visual pipeline stages
        </Text>
      </div>

      {/* Advanced Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        {/* Filter Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: filtersExpanded ? 16 : 0 }}>
          <Col flex="auto">
            <Input
              placeholder="Search clients by name or email..."
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              allowClear
              style={{ marginRight: 16 }}
            />
          </Col>
          <Col>
            <Space>
              <Text type="secondary">
                {filteredClients.length} of {clients.length} prospects
              </Text>
              <Button
                type="text"
                icon={<FilterOutlined />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                Advanced Filters
                {filtersExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
              </Button>
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
                disabled={!filters.searchText && 
                  filters.stages.length === 0 && 
                  filters.priorities.length === 0 &&
                  filters.tiers.length === 0 &&
                  filters.coaches.length === 0 &&
                  !filters.daysInStageRange &&
                  !filters.valueRange}
              >
                Clear All
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Expandable Filter Panel */}
        <Collapse 
          ghost 
          activeKey={filtersExpanded ? ['filters'] : []}
          items={[
            {
              key: 'filters',
              showArrow: false,
              children: (
                <div>
                  <Row gutter={[16, 16]}>
                    {/* Stage Filter */}
                    <Col xs={24} sm={12} md={6}>
                      <Text strong>Pipeline Stage:</Text>
                      <Checkbox.Group
                        value={filters.stages}
                        onChange={(values) => setFilters(prev => ({ ...prev, stages: values as string[] }))}
                        style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}
                      >
                        <Checkbox value="leads">New Leads</Checkbox>
                        <Checkbox value="contract">Contract Pending</Checkbox>
                        <Checkbox value="payment">Payment Pending</Checkbox>
                        <Checkbox value="onboarding">Onboarding</Checkbox>
                        <Checkbox value="completed">Completed</Checkbox>
                        <Checkbox value="stalled">Stalled/Issues</Checkbox>
                      </Checkbox.Group>
                    </Col>

                    {/* Priority Filter */}
                    <Col xs={24} sm={12} md={6}>
                      <Text strong>Priority:</Text>
                      <Checkbox.Group
                        value={filters.priorities}
                        onChange={(values) => setFilters(prev => ({ ...prev, priorities: values as string[] }))}
                        style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}
                      >
                        <Checkbox value="high">High Priority</Checkbox>
                        <Checkbox value="medium">Medium Priority</Checkbox>
                        <Checkbox value="low">Low Priority</Checkbox>
                      </Checkbox.Group>
                    </Col>

                    {/* Tier Filter */}
                    <Col xs={24} sm={12} md={6}>
                      <Text strong>Client Tier:</Text>
                      <Checkbox.Group
                        value={filters.tiers}
                        onChange={(values) => setFilters(prev => ({ ...prev, tiers: values as string[] }))}
                        style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}
                      >
                        <Checkbox value="Basic">Basic</Checkbox>
                        <Checkbox value="Standard">Standard</Checkbox>
                        <Checkbox value="Premium">Premium</Checkbox>
                        <Checkbox value="Elite">Elite</Checkbox>
                      </Checkbox.Group>
                    </Col>

                    {/* Coach Filter */}
                    <Col xs={24} sm={12} md={6}>
                      <Text strong>Assigned Coach:</Text>
                      <Select
                        mode="multiple"
                        placeholder="Select coaches"
                        style={{ width: '100%', marginTop: 8 }}
                        value={filters.coaches}
                        onChange={(values) => setFilters(prev => ({ ...prev, coaches: values }))}
                        allowClear
                      >
                        <Option value="Sarah Johnson">Sarah Johnson</Option>
                        <Option value="Mike Chen">Mike Chen</Option>
                        <Option value="Emily Davis">Emily Davis</Option>
                      </Select>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    {/* Quick Filters */}
                    <Col xs={24} sm={12} md={12}>
                      <Text strong>Quick Filters:</Text>
                      <div style={{ marginTop: 8 }}>
                        <Button
                          size="small"
                          type={filters.daysInStageRange && filters.daysInStageRange[0] === 7 && filters.daysInStageRange[1] === 100 ? 'primary' : 'default'}
                          onClick={() => setFilters(prev => ({ 
                            ...prev, 
                            daysInStageRange: prev.daysInStageRange && prev.daysInStageRange[0] === 7 && prev.daysInStageRange[1] === 100 ? null : [7, 100]
                          }))}
                          style={{ marginBottom: 8, width: '100%' }}
                        >
                          Stalled (7+ days)
                        </Button>
                        <br />
                        <Button
                          size="small"
                          type={filters.valueRange && filters.valueRange[0] === 300 && filters.valueRange[1] === 1000 ? 'primary' : 'default'}
                          onClick={() => setFilters(prev => ({ 
                            ...prev, 
                            valueRange: prev.valueRange && prev.valueRange[0] === 300 && prev.valueRange[1] === 1000 ? null : [300, 1000]
                          }))}
                          style={{ width: '100%' }}
                        >
                          High Value ($300+)
                        </Button>
                      </div>
                    </Col>


                  </Row>
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Prospects"
              value={totalClients}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Pipeline Value"
              value={totalValue}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Avg. Days/Stage"
              value={avgDaysInStage}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              suffix="days"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Conversion Rate"
              value={conversionRate}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

             {/* Alert for stalled clients */}
       {stages.find(s => s.key === 'stalled')?.clients.length! > 0 && (
         <Alert
           message={`${stages.find(s => s.key === 'stalled')?.clients.length} clients are stalled and need attention`}
           type="warning"
           showIcon
           closable
           style={{ marginBottom: 16 }}
           action={
             <Button size="small" type="primary" onClick={handleReviewStalled}>
               Review Details
             </Button>
           }
         />
       )}

      {/* Pipeline Board */}
      <Row gutter={[16, 16]}>
        {stages.map((stage) => (
          <Col xs={24} sm={12} md={8} lg={4} key={stage.key}>
            <Card
              title={
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ color: stage.color, marginRight: 8 }}>
                      {stage.icon}
                    </span>
                    <Text strong>{stage.title}</Text>
                  </div>
                  <Text style={{ fontSize: 12, color: '#666', fontWeight: 'normal' }}>
                    {stage.description}
                  </Text>
                </div>
              }
              extra={
                <Badge 
                  count={stage.clients.length} 
                  style={{ backgroundColor: stage.color }}
                />
              }
              style={{ 
                minHeight: 400,
                borderTop: `3px solid ${stage.color}`,
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {stage.clients.map((client) => (
                  <ClientCard key={client.id} client={client} />
                ))}
                
                {stage.clients.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px 0', 
                    color: '#999' 
                  }}>
                    <Text type="secondary">No clients in this stage</Text>
                  </div>
                )}
              </div>

              {stage.key === 'leads' && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  style={{ width: '100%', marginTop: 8 }}
                  size="small"
                >
                  Add New Lead
                </Button>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Client Detail Modal */}
      <Modal
        title={selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : ''}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          <Button key="edit" type="primary">
            Edit Client
          </Button>,
        ]}
      >
        {selectedClient && (
          <div>
            {/* Client Info */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>Email:</Text>
                <br />
                <Text>{selectedClient.email}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Phone:</Text>
                <br />
                <Text>{selectedClient.phone}</Text>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>Tier:</Text>
                <br />
                <Tag color={selectedClient.tier === 'Elite' ? 'red' : selectedClient.tier === 'Premium' ? 'gold' : 'blue'}>
                  {selectedClient.tier}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>Value:</Text>
                <br />
                <Text strong>${selectedClient.value}</Text>
              </Col>
            </Row>

            {selectedClient.assignedCoach && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Text strong>Assigned Coach:</Text>
                  <br />
                  <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <Text>{selectedClient.assignedCoach}</Text>
                  </Space>
                </Col>
              </Row>
            )}

            {/* Progress Steps */}
            <div style={{ marginBottom: 16 }}>
              <Text strong>Onboarding Progress:</Text>
              <Steps
                direction="vertical"
                size="small"
                current={selectedClient.completedSteps.length - 1}
                style={{ marginTop: 12 }}
              >
                {onboardingSteps.map((step, index) => (
                  <Steps.Step
                    key={step}
                    title={step}
                    status={
                      selectedClient.completedSteps.includes(step) 
                        ? 'finish' 
                        : index === selectedClient.completedSteps.length 
                          ? 'process' 
                          : 'wait'
                    }
                  />
                ))}
              </Steps>
            </div>

            {/* Notes */}
            <div>
              <Text strong>Notes:</Text>
              <br />
              <Text>{selectedClient.notes}</Text>
            </div>
                     </div>
         )}
       </Modal>

       {/* Stalled Clients Review Modal */}
       <Modal
         title={
           <Space>
             <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
             <span>Stalled Clients Review</span>
           </Space>
         }
         open={showStalledModal}
         onCancel={() => setShowStalledModal(false)}
         width={800}
         footer={[
           <Button key="close" onClick={() => setShowStalledModal(false)}>
             Close
           </Button>,
           <Button key="action" type="primary">
             Take Action
           </Button>,
         ]}
       >
         <div>
           <Alert
             message="These clients have been stalled for more than 5 days and require immediate attention"
             type="warning"
             showIcon
             style={{ marginBottom: 16 }}
           />
           
           <List
             itemLayout="horizontal"
             dataSource={filteredClients.filter(c => c.stage === 'stalled')}
             renderItem={(client: OnboardingClient) => (
               <List.Item
                 actions={[
                   <Button key="contact" size="small" icon={<MailOutlined />}>
                     Contact
                   </Button>,
                   <Button key="details" size="small" onClick={() => {
                     setSelectedClient(client);
                     setShowStalledModal(false);
                     setIsModalVisible(true);
                   }}>
                     View Details
                   </Button>
                 ]}
               >
                 <List.Item.Meta
                   avatar={<Avatar icon={<UserOutlined />} />}
                   title={
                     <Space>
                       <Text strong>{client.firstName} {client.lastName}</Text>
                       <Tag color="red">Stalled {client.daysInStage} days</Tag>
                       <Tag color="gold">{client.tier}</Tag>
                     </Space>
                   }
                   description={
                     <div>
                       <div style={{ marginBottom: 4 }}>
                         <Text>Email: {client.email} | Phone: {client.phone}</Text>
                       </div>
                       <div style={{ marginBottom: 4 }}>
                         <Text>Value: ${client.value} | Progress: {client.progress}%</Text>
                       </div>
                       <div>
                         <Text type="secondary">Notes: {client.notes}</Text>
                       </div>
                     </div>
                   }
                 />
               </List.Item>
             )}
           />
         </div>
       </Modal>
     </div>
   );
 };
 
 export default OnboardingPipeline; 